/**
 * Inventory forecast — Stage 5.
 * Sources `/api/bi/inventory/forecast/`.
 */
import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { DataView } from '@/components/data/DataView';
import { KPICard } from '@/components/charts/KPICard';
import { TaxonomyRail } from '@/components/inventory/TaxonomyRail';
import {
  useInventoryForecast,
  useInventoryTaxonomy,
  type ForecastRow,
  type ForecastUrgency,
} from '@/api/bi';
import { useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

const URG_TONE: Record<ForecastUrgency, 'danger' | 'warning' | 'info' | 'success'> = {
  out: 'danger',
  critical: 'danger',
  low: 'warning',
  ok: 'success',
};

const URG_LABEL: Record<ForecastUrgency, string> = {
  out: 'out of stock',
  critical: 'critical',
  low: 'low cover',
  ok: 'healthy',
};

const HORIZONS = [7, 14, 30] as const;

export function ForecastPage() {
  const storeIds = useFilters((s) => s.storeIds);
  const [horizon, setHorizon] = useState<(typeof HORIZONS)[number]>(14);
  const [search, setSearch] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState<ForecastUrgency | null>(null);

  // Taxonomy multi-select cascade — same shape as the ledger /
  // replenishment pages so the operator's mental model carries over.
  const [vendorIds, setVendorIds] = useState<number[]>([]);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [categoryGroupIds, setCategoryGroupIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<number[]>([]);
  const [productQuery, setProductQuery] = useState('');

  const taxonomy = useInventoryTaxonomy();
  const query = useInventoryForecast({
    storeIds,
    horizon,
    limit: 200,
    vendorIds,
    brandIds,
    categoryGroupIds,
    categoryIds,
    subcategoryIds,
    q: productQuery.trim() || undefined,
  });

  const all = query.data?.rows ?? [];
  // Local search/urgency filters layer on top of the server-side rail —
  // the rail already constrains the result set, this just lets the user
  // pivot inside it without a network round-trip.
  const filtered = useMemo(() => {
    let rows = all;
    if (urgencyFilter) rows = rows.filter((r) => r.urgency === urgencyFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(
        (r) =>
          r.product_name.toLowerCase().includes(q) ||
          (r.brand ?? '').toLowerCase().includes(q) ||
          (r.store_name ?? '').toLowerCase().includes(q),
      );
    }
    return rows;
  }, [all, urgencyFilter, search]);

  const columns = useMemo<ColumnDef<ForecastRow, unknown>[]>(
    () => [
      {
        id: 'product',
        header: 'Product',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{row.original.product_name}</div>
            <div className="text-[11px] text-muted-foreground">
              {row.original.brand ?? '—'} · {row.original.store_name ?? '—'}
            </div>
          </div>
        ),
      },
      {
        id: 'urgency',
        header: 'Status',
        cell: ({ row }) => (
          <Badge tone={URG_TONE[row.original.urgency]} className="gap-1">
            {row.original.urgency === 'out' || row.original.urgency === 'critical' ? (
              <AlertTriangle className="h-3 w-3" />
            ) : null}
            {URG_LABEL[row.original.urgency]}
          </Badge>
        ),
      },
      {
        id: 'available',
        header: () => <div className="text-right">On hand</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {fmt.number(row.original.available)}
          </div>
        ),
      },
      {
        id: 'cover',
        header: () => <div className="text-right">Cover (days)</div>,
        cell: ({ row }) => {
          const v = row.original.cover_days;
          if (v == null) return <div className="text-right text-xs text-muted-foreground">—</div>;
          return (
            <div className="kpi-num text-right text-sm">
              {v.toFixed(1)}
            </div>
          );
        },
      },
      {
        id: 'demand',
        header: () => <div className="text-right">Daily demand</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{row.original.daily_demand.toFixed(2)}</div>
        ),
      },
      {
        id: 'lead',
        header: () => <div className="text-right">Lead</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">
            {row.original.lead_time_days != null ? `${row.original.lead_time_days}d` : '—'}
          </div>
        ),
      },
      {
        id: 'suggest',
        header: () => <div className="text-right">Suggest</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {row.original.suggested_quantity != null ? fmt.number(row.original.suggested_quantity) : '—'}
          </div>
        ),
      },
      {
        id: 'lastoos',
        header: 'Last OOS',
        cell: ({ row }) =>
          row.original.last_out_of_stock_date ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.last_out_of_stock_date), 'MMM d')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
    ],
    [],
  );

  const totals = query.data?.totals;

  return (
    <>
      <PageHeader
        eyebrow="Supply"
        title="Inventory forecast"
        description="Days-of-cover and replenishment urgency by product/store. Sorted so the worst surfaces first."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 5
          </Badge>
        }
      />

      <div className="mt-4">
        <TaxonomyRail
          taxonomy={taxonomy.data}
          taxonomyLoading={taxonomy.isLoading}
          vendorIds={vendorIds}
          onVendorIds={setVendorIds}
          brandIds={brandIds}
          onBrandIds={setBrandIds}
          categoryGroupIds={categoryGroupIds}
          onCategoryGroupIds={setCategoryGroupIds}
          categoryIds={categoryIds}
          onCategoryIds={setCategoryIds}
          subcategoryIds={subcategoryIds}
          onSubcategoryIds={setSubcategoryIds}
          q={productQuery}
          onQ={setProductQuery}
          onClear={() => {
            setVendorIds([]);
            setBrandIds([]);
            setCategoryGroupIds([]);
            setCategoryIds([]);
            setSubcategoryIds([]);
            setProductQuery('');
          }}
        />
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard label="Rows shown" value={fmt.number(totals?.rows ?? 0)} loading={query.isLoading} />
        <KPICard
          label="At risk"
          value={fmt.number(totals?.at_risk ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
          hint="Out / critical / low cover."
        />
        <KPICard
          label="Out now"
          value={fmt.number(totals?.out_now ?? 0)}
          goodWhenUp={false}
          loading={query.isLoading}
        />
        <KPICard
          label="Horizon"
          value={`${horizon}d`}
          loading={false}
          hint="Used for the cover-days threshold buckets."
        />
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load inventory forecast." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<ForecastRow>
            title="Most at-risk SKUs"
            data={filtered}
            columns={columns}
            isLoading={query.isLoading}
            search={{ value: search, onChange: setSearch, placeholder: 'Search product, brand or store…' }}
            emptyTitle={urgencyFilter ? `No "${URG_LABEL[urgencyFilter]}" rows` : 'No forecast data yet'}
            toolbar={
              <div className="flex flex-wrap items-center gap-1.5">
                {(['out', 'critical', 'low', 'ok'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUrgencyFilter((cur) => (cur === u ? null : u))}
                    className={cn(
                      'press rounded-full border px-2.5 py-1 text-[11px] focus-ring',
                      urgencyFilter === u
                        ? 'border-primary/30 bg-primary/10 text-primary'
                        : 'border-border bg-surface text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {URG_LABEL[u]}
                  </button>
                ))}
                <span className="mx-1 h-4 w-px bg-border" aria-hidden />
                {HORIZONS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setHorizon(h)}
                    className={cn(
                      'press rounded-full border px-2.5 py-1 text-[11px] focus-ring',
                      horizon === h
                        ? 'border-primary/30 bg-primary/10 text-primary'
                        : 'border-border bg-surface text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {h}d
                  </button>
                ))}
              </div>
            }
          />
        )}
      </div>
    </>
  );
}
